/**
 * Local Mode Context — STUB
 *
 * Private Guest Mode has been removed. This stub preserves the exported API
 * so the ~45 consumers that call useLocalMode() keep compiling.
 * isLocalMode is always false; every action is a no-op.
 *
 * TODO: Remove useLocalMode() calls from consumer files during cleanup.
 */

import { createContext, useContext, type ReactNode } from 'react';

interface LocalModeContextType {
  isLocalMode: boolean;
  isLoading: boolean;
  displayName: string | null;
  isPrivateBrowsingMode: boolean;
  enterLocalMode: (displayName: string) => Promise<void>;
  resumeLocalMode: () => Promise<boolean>;
  exitLocalMode: () => Promise<void>;
  exportData: () => Promise<Record<string, never>>;
  importData: (data: Record<string, unknown>) => Promise<void>;
  checkLocalModeStatus: () => Promise<boolean>;
}

const NOOP_CONTEXT: LocalModeContextType = {
  isLocalMode: false,
  isLoading: false,
  displayName: null,
  isPrivateBrowsingMode: false,
  enterLocalMode: async () => {},
  resumeLocalMode: async () => false,
  exitLocalMode: async () => {},
  exportData: async () => ({}),
  importData: async () => {},
  checkLocalModeStatus: async () => false,
};

const LocalModeContext = createContext<LocalModeContextType>(NOOP_CONTEXT);

/** Pass-through provider — keeps existing <LocalModeProvider> wrappers compiling. */
export function LocalModeProvider({ children }: { children: ReactNode }) {
  return (
    <LocalModeContext.Provider value={NOOP_CONTEXT}>
      {children}
    </LocalModeContext.Provider>
  );
}

/** Always returns isLocalMode: false. Safe to call anywhere. */
export function useLocalMode(): LocalModeContextType {
  return useContext(LocalModeContext);
}

/** Always returns { isAvailable: true }. */
export function useLocalModeFeature(_featureName: string): { isAvailable: boolean; reason?: string } {
  return { isAvailable: true };
}
